import type { CreateFlowPayload } from '@polyai/stream/commands/handlers/flow/create-flow/schema';
import type { CreateNoCodeConditionPayload } from '@polyai/stream/commands/handlers/flow/create-no-code-condition/schema';
import type { CreateStepPayload } from '@polyai/stream/commands/handlers/flow/create-step/schema';
import {
  type Flow,
  flowFunctionsSelectors,
  selectAdvancedSteps,
  selectDefaultSteps,
  selectFunctionSteps,
} from '@polyai/stream/store/reducers/flows';
import {
  ConditionType,
  IngressPosition,
} from '@polyai/stream/store/reducers/flows/types';
import { noCodeStepReferencesAdapter } from '@polyai/stream/store/reducers/flows/utils';
import {
  delayResponsesSelectors,
  parametersSelectors,
} from '@polyai/stream/store/reducers/functions';

import {
  generateConditionId,
  generateDelayResponseId,
  generateFlowId,
  generateFunctionStepId,
  generateNoCodeStepId,
  generateParameterId,
  generateStepId,
  generateTransitionId,
} from 'lib/prettyUuid';
import { TRANSITION_FUNCTION_CODE } from './constants';
import { FLOWS } from './Flows.uiCopy';

const DEFAULT_FLOW_NAME = 'flow';

// Default positioning constants for new flow creation
const FLOW_CREATION_POSITIONS = {
  noCodeStep: { x: 0, y: 0 },
  condition: { x: 162, y: 300 },
  exitFlow: { x: 183, y: 450 },
} as const;

// Overloaded function signatures for better TypeScript inference
export function getNewFlow(params: {
  isNoCodeEnabled: true;
  flows: Flow[];
  preferredName?: string;
}): {
  flowPayload: CreateFlowPayload;
  noCodeConditionPayload: CreateNoCodeConditionPayload;
};

export function getNewFlow(params: {
  isNoCodeEnabled: false;
  flows: Flow[];
  preferredName?: string;
}): CreateFlowPayload;

export function getNewFlow({
  isNoCodeEnabled,
  flows,
  preferredName,
}: {
  isNoCodeEnabled: boolean;
  flows: Flow[];
  preferredName?: string;
}):
  | CreateFlowPayload
  | {
      flowPayload: CreateFlowPayload;
      noCodeConditionPayload: CreateNoCodeConditionPayload;
    } {
  if (isNoCodeEnabled) {
    return getNoCodeEnabledNewFlow(flows, preferredName);
  }
  return getNoCodeDisabledNewFlow(flows, preferredName);
}

const getNoCodeEnabledNewFlow = (
  flows: Flow[],
  preferredName: string = DEFAULT_FLOW_NAME,
): {
  flowPayload: CreateFlowPayload;
  noCodeConditionPayload: CreateNoCodeConditionPayload;
} => {
  const flowId = generateFlowId();
  const startStepId = generateNoCodeStepId();

  const defaultSteps: CreateFlowPayload['noCodeSteps'] = [
    {
      flowId,
      stepId: startStepId,
      name: FLOWS.creation.startStep.name,
      prompt: 'Collect the callers address, first name and last name',
      position: { x: 0, y: 0 },
      references: noCodeStepReferencesAdapter.createInitialState(),
    },
  ];

  const name = getUniqueName(flows?.map(f => f.name) ?? [], preferredName);

  const flowPayload: CreateFlowPayload = {
    id: flowId,
    name,
    description: `Description for flow ${name}`,
    startStepId,
    steps: [], // No advanced steps - using No Code Steps instead
    noCodeSteps: defaultSteps,
    transitionFunctions: [], // No transition functions needed
  };

  // Create default condition (always created since we always have no-code steps)
  const noCodeConditionPayload: CreateNoCodeConditionPayload = {
    flowId,
    stepId: startStepId,
    conditionId: generateConditionId(),
    config: {
      $case: ConditionType.ExitFlowCondition,
      value: {
        details: {
          label: FLOWS.creation.defaultCondition.label,
          description: FLOWS.creation.defaultCondition.description,
          position: FLOW_CREATION_POSITIONS.condition,
          requiredEntities: [],
          ingressPosition: IngressPosition.Top,
        },
        exitFlowPosition: FLOW_CREATION_POSITIONS.exitFlow,
      },
    },
  };

  return { flowPayload, noCodeConditionPayload };
};

const getNoCodeDisabledNewFlow = (
  flows: Flow[],
  preferredName: string = DEFAULT_FLOW_NAME,
): CreateFlowPayload => {
  const flowId = generateFlowId();
  const startStepId = generateStepId();
  const isFirstFlow = flows.length === 0;

  let steps: CreateFlowPayload['steps'] = [
    {
      id: startStepId,
      name: FLOWS.creation.startStep.name,
      prompt: '',
      position: { x: 0, y: 0 },
      references: {
        attributes: {},
        handoff: {},
        sms: {},
        transitionFunctions: {},
        globalFunctions: {},
        entities: {},
        variables: {},
      },
    },
  ];

  let transitionFunctions: CreateFlowPayload['transitionFunctions'] = [];

  if (isFirstFlow) {
    // Create additional step and functions for first flow
    const endStepId = generateStepId();
    const startFunctionId = generateTransitionId();
    const endFunctionId = generateTransitionId();

    steps = [
      {
        id: startStepId,
        name: FLOWS.creation.startStep.name,
        prompt: `Collect the users name and then call {{ft:${startFunctionId}}} to continue.`,
        position: { x: 0, y: 0 },
        references: {
          attributes: {},
          handoff: {},
          sms: {},
          transitionFunctions: { [startFunctionId]: true },
          globalFunctions: {},
          entities: {},
          variables: {},
        },
      },
      {
        id: endStepId,
        name: FLOWS.creation.endStep.name,
        prompt: `{{ft:${endFunctionId}}}`,
        position: { x: 0, y: 205 },
        references: {
          attributes: {},
          handoff: {},
          sms: {},
          transitionFunctions: { [endFunctionId]: true },
          globalFunctions: {},
          entities: {},
          variables: {},
        },
      },
    ];

    transitionFunctions = [
      {
        id: startFunctionId,
        archived: false,
        name: 'transition_to_end_step',
        description: FLOWS.creation.transitionFunction.start.description,
        code: TRANSITION_FUNCTION_CODE.TRANSITION_TO_END_STEP,
        parameters: [
          {
            id: generateParameterId(),
            type: 'string',
            name: 'name',
            description:
              FLOWS.creation.transitionFunction.start.parameter.description,
          },
        ],
        errors: [],
        references: {
          flowSteps: {
            [startStepId]: true,
          },
          variables: {},
        },
      },
      {
        id: endFunctionId,
        archived: false,
        name: 'exit_flow',
        description: FLOWS.creation.transitionFunction.end.description,
        code: TRANSITION_FUNCTION_CODE.EXIT_FLOW,
        parameters: [],
        errors: [],
        references: {
          flowSteps: {
            [endStepId]: true,
          },
          variables: {},
        },
      },
    ];
  }

  const name = getUniqueName(flows?.map(f => f.name) ?? [], preferredName);

  return {
    id: flowId,
    name,
    description: FLOWS.creation.description(name),
    startStepId,
    steps,
    transitionFunctions,
    noCodeSteps: [],
  };
};

const getUniqueName = (
  names: string[],
  defaultName: string = DEFAULT_FLOW_NAME,
  separator: string = ' ',
) => {
  let i = 1;
  let name = defaultName;
  while (names?.find(fname => fname === name)) {
    name = `${defaultName}${separator}${i}`;
    i++;
  }
  return name;
};

type DuplicateFlowResult = {
  flowPayload: CreateFlowPayload;
  idMapping: Record<string, string>;
};

export const duplicateFlowNoCodeDisabled = (
  flow: Flow,
  flows: Flow[],
): DuplicateFlowResult => {
  const newFlowId = generateFlowId();
  const idMapping: Record<string, string> = {};

  const existingAdvancedSteps = selectAdvancedSteps(flow);
  existingAdvancedSteps.forEach(step => {
    const newStepId = generateStepId();
    idMapping[step.id] = newStepId;
  });

  const existingFunctions = flowFunctionsSelectors
    .selectAll(flow.transitionFunctions)
    .filter(func => !func.archived);
  existingFunctions.forEach(func => {
    const newFunctionId = generateTransitionId();
    idMapping[func.id] = newFunctionId;
  });

  const existingDefaultSteps = selectDefaultSteps(flow);
  existingDefaultSteps.forEach(defaultStep => {
    const newDefaultStepId = generateNoCodeStepId();
    idMapping[defaultStep.id] = newDefaultStepId;
  });

  const existingFunctionSteps = selectFunctionSteps(flow);
  existingFunctionSteps.forEach(functionStep => {
    const newFunctionStepId = generateFunctionStepId();
    idMapping[functionStep.id] = newFunctionStepId;
  });

  const duplicatedSteps: CreateFlowPayload['steps'] = existingAdvancedSteps.map(
    ({
      updatedAt: _,
      updatedBy: __,
      createdAt: ___,
      createdBy: ____,
      references,
      ...step
    }) => ({
      ...step,
      id: idMapping[step.id],
      prompt: step.prompt.replace(
        /\{\{ft:([^}]+)\}\}/g,
        (_match, functionId) =>
          `{{ft:${
            idMapping[functionId] ??
            flowFunctionsSelectors.selectById(
              flow.transitionFunctions,
              functionId,
            )?.name ??
            'unknown'
          }}}`,
      ),
      references: {
        sms: references?.sms || {},
        handoff: references?.handoff || {},
        attributes: references?.attributes || {},
        entities: references?.entities || {},
        variables: references?.variables || {},
        transitionFunctions: Object.keys(
          references?.transitionFunctions || {},
        ).reduce((acc, funcId) => {
          if (!idMapping[funcId]) {
            return acc;
          }

          return {
            ...acc,
            [idMapping[funcId]]: true,
          };
        }, {}),
        globalFunctions: references.globalFunctions ?? {},
      },
    }),
  );

  const duplicatedDefaultSteps: CreateFlowPayload['noCodeSteps'] =
    existingDefaultSteps.map(
      ({
        updatedAt: _,
        updatedBy: __,
        createdAt: ___,
        createdBy: ____,
        references,
        ...defaultStep
      }) => ({
        ...defaultStep,
        flowId: newFlowId,
        stepId: idMapping[defaultStep.id],
        references: noCodeStepReferencesAdapter.createInitialState(references),
      }),
    );

  const duplicatedFunctions: CreateFlowPayload['transitionFunctions'] =
    existingFunctions.map(func => {
      const {
        createdAt: _,
        createdBy: __,
        updatedAt: ___,
        updatedBy: ____,
        delayResponses,
        ...latencyControl
      } = func.latencyControl;
      return {
        ...func,
        id: idMapping[func.id],
        parameters: parametersSelectors
          .selectAll(func.parameters)
          .map(param => ({
            ...param,
            type: param.type as 'string' | 'number' | 'boolean',
            id: generateParameterId(),
          })),
        errors: func.errors,
        latencyControl: {
          ...latencyControl,
          delayResponses: delayResponsesSelectors
            .selectAll(delayResponses)
            .map(
              ({
                createdAt: _,
                createdBy: __,
                updatedAt: ___,
                updatedBy: ____,
                ...delayResponse
              }) => ({ ...delayResponse, id: generateDelayResponseId() }),
            ),
        },
        references: {
          flowSteps: Object.entries(func.references?.flowSteps || {}).reduce(
            (acc, [stepId, value]) => {
              if (!idMapping[stepId]) {
                return acc;
              }

              return {
                ...acc,
                [idMapping[stepId]]: value,
              };
            },
            {},
          ),
          variables: func.references?.variables || {}, // TODO test this
        },
      };
    });

  const newName = getUniqueName(
    flows.map(f => f.name),
    flow.name,
  );

  return {
    flowPayload: {
      id: newFlowId,
      name: newName,
      // Fallback to default description if original flow has no description
      description: flow.description || FLOWS.creation.description(newName),
      startStepId: idMapping[flow.startStepId],
      steps: duplicatedSteps,
      noCodeSteps: duplicatedDefaultSteps,
      transitionFunctions: duplicatedFunctions,
    },
    idMapping,
  };
};

export const duplicateFlowNoCodeEnabled = (
  flow: Flow,
  flows: Flow[],
): {
  flowPayload: CreateFlowPayload;
  noCodeConditionPayloads: CreateNoCodeConditionPayload[];
  functionStepPayloads: CreateStepPayload[];
} => {
  const { flowPayload, idMapping } = duplicateFlowNoCodeDisabled(flow, flows);

  // Create function step payloads
  const existingFunctionSteps = selectFunctionSteps(flow);
  const functionStepPayloads: CreateStepPayload[] = existingFunctionSteps.map(
    functionStep => {
      const latencyControl = functionStep.function.latencyControl;
      const {
        createdAt: _lc1,
        createdBy: _lc2,
        updatedAt: _lc3,
        updatedBy: _lc4,
        delayResponses,
        ...latencyControlRest
      } = latencyControl;

      return {
        flowId: flowPayload.id,
        payload: {
          $case: 'functionStep' as const,
          value: {
            id: idMapping[functionStep.id],
            name: functionStep.name,
            position: functionStep.position,
            function: {
              id: functionStep.function.id,
              name: functionStep.function.name,
              code: functionStep.function.code,
              archived: functionStep.function.archived,
              errors: functionStep.function.errors ?? [],
              latencyControl: {
                ...latencyControlRest,
                delayResponses: delayResponsesSelectors
                  .selectAll(delayResponses)
                  .map(
                    ({
                      createdAt: _dr1,
                      createdBy: _dr2,
                      updatedAt: _dr3,
                      updatedBy: _dr4,
                      ...delayResponse
                    }) => ({ ...delayResponse, id: generateDelayResponseId() }),
                  ),
              },
            },
          },
        },
      };
    },
  );

  return {
    flowPayload,
    noCodeConditionPayloads: duplicateNoCodeCondition(
      flow,
      flowPayload,
      idMapping,
    ),
    functionStepPayloads,
  };
};

export const duplicateNoCodeCondition = (
  flow: Flow,
  flowPayload: CreateFlowPayload,
  idMapping: Record<string, string>,
): CreateNoCodeConditionPayload[] => {
  const newFlowId = flowPayload.id;

  // Get all steps that have conditions (default steps and function steps)
  const existingDefaultSteps = selectDefaultSteps(flow);
  const existingFunctionSteps = selectFunctionSteps(flow);

  // Traverse all steps with conditions and collect their conditions
  const conditionPayloads: CreateNoCodeConditionPayload[] = [];

  // Helper function to process conditions for a step
  const processStepConditions = (
    stepId: string,
    conditions: (typeof existingDefaultSteps)[0]['conditions'],
  ) => {
    const newStepId = idMapping[stepId];
    if (!newStepId) {
      return;
    }

    conditions.forEach(condition => {
      const newConditionId = generateConditionId();

      let config: CreateNoCodeConditionPayload['config'];

      if (condition.config.$case === ConditionType.ExitFlowCondition) {
        config = {
          $case: ConditionType.ExitFlowCondition,
          value: {
            details: condition.config.value.details,
            exitFlowPosition: condition.config.value.exitFlowPosition,
          },
        };
      } else if (
        condition.config.$case === ConditionType.NoCodeStepCondition ||
        condition.config.$case === ConditionType.StepCondition ||
        condition.config.$case === ConditionType.FunctionStepCondition
      ) {
        // Update childStepId to the new mapped step ID
        const newChildStepId =
          idMapping[condition.config.value.childStepId] ??
          condition.config.value.childStepId;

        config = {
          $case: condition.config.$case,
          value: {
            details: condition.config.value.details,
            childStepId: newChildStepId,
          },
        };
      } else {
        // Fallback - should not happen but TypeScript requires it
        config = condition.config;
      }

      const conditionPayload: CreateNoCodeConditionPayload = {
        flowId: newFlowId,
        stepId: newStepId,
        conditionId: newConditionId,
        config,
      };

      conditionPayloads.push(conditionPayload);
    });
  };

  // Process conditions from default steps
  existingDefaultSteps.forEach(defaultStep => {
    processStepConditions(defaultStep.id, defaultStep.conditions);
  });

  // Process conditions from function steps
  existingFunctionSteps.forEach(functionStep => {
    processStepConditions(functionStep.id, functionStep.conditions);
  });

  return conditionPayloads;
};
